import React, { useState } from "react";
import { ClientApartment } from "../../../utils/api/apartmentRentalsAPI";
import { AMENITY_ICONS, AVATAR_COLORS } from "./bookingPricing";
import PhotoGallery from "./PhotoGallery";

interface Props {
  apt: ClientApartment;
  onOpenLightbox: (index: number) => void;
  aboutLabel: string;
  amenitiesLabel: string;
  readMoreLabel: string;
  showLessLabel: string;
  guestsLabel: string;
  typeLabel: string;
}

const typeName = (type: ClientApartment["type"], typeLabel: string) => {
  switch (type) {
    case "studio": return "Studio";
    case "apartment": return "Apartman";
    case "loft": return "Loft";
    case "villa": return "Vila";
    case "house": return "Kuća";
    default: return typeLabel;
  }
};

const ApartmentSummaryCard: React.FC<Props> = ({
  apt, onOpenLightbox, aboutLabel, amenitiesLabel, readMoreLabel, showLessLabel, guestsLabel, typeLabel,
}) => {
  const [descExpanded, setDescExpanded] = useState(false);
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const hasImages = apt.images && apt.images.length > 0;
  const longDesc = (apt.description?.length || 0) > 180;
  const visibleAmenities = amenitiesExpanded ? apt.amenities : apt.amenities.slice(0, 6);

  const coverStyle = {
    background: apt.coverGradient || `linear-gradient(135deg, ${AVATAR_COLORS[0]}, ${AVATAR_COLORS[3]})`,
  };

  return (
    <div className="pab-apt-card">
      {hasImages ? (
        <PhotoGallery images={apt.images} name={apt.name} onOpen={onOpenLightbox} />
      ) : (
        <div className="pab-apt-card__cover" style={coverStyle} />
      )}

      <div className="pab-apt-card__body">
        <div className="pab-apt-card__eyebrow">📍 {apt.location}</div>
        <h1 className="pab-apt-card__name">{apt.name}</h1>
        <div className="pab-apt-card__meta">
          <span>👤 {guestsLabel} {apt.capacity}</span>
          <span>{typeName(apt.type, typeLabel)}</span>
        </div>
        {apt.tags?.length > 0 && (
          <div className="pab-apt-card__tags">
            {apt.tags.map((t) => <span key={t} className="pab-tag">{t}</span>)}
          </div>
        )}

        {apt.description && (
          <div className="pab-apt-card__section">
            <div className="pab-apt-card__section-title">{aboutLabel}</div>
            <div className={`pab-apt-card__desc${descExpanded ? "" : " pab-apt-card__desc--clamped"}`}>
              {apt.description}
            </div>
            {longDesc && (
              <button type="button" className="pab-link-btn" onClick={() => setDescExpanded((v) => !v)}>
                {descExpanded ? showLessLabel : readMoreLabel}
              </button>
            )}
          </div>
        )}

        {apt.amenities?.length > 0 && (
          <div className="pab-apt-card__section">
            <div className="pab-apt-card__section-title">{amenitiesLabel}</div>
            <div className="pab-amen-grid">
              {visibleAmenities.map((a) => (
                <div key={a} className="pab-amen-item">
                  <span className="ic">{AMENITY_ICONS[a] || "✓"}</span>
                  <span>{a}</span>
                </div>
              ))}
            </div>
            {apt.amenities.length > 6 && (
              <button type="button" className="pab-link-btn" onClick={() => setAmenitiesExpanded((v) => !v)}>
                {amenitiesExpanded ? showLessLabel : `${readMoreLabel} (${apt.amenities.length - 6})`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApartmentSummaryCard;
